import { useState, useEffect, useRef } from "react";
import { useHotkeys } from "react-hotkeys-hook";
import { LuInfo, LuSearch, LuX } from "react-icons/lu";
import toast from "react-hot-toast";
import { useFloating, autoUpdate, offset, flip, shift, size, useClick, useDismiss, useRole, useInteractions, FloatingPortal } from "@floating-ui/react";
import { useQuery, keepPreviousData } from "@tanstack/react-query";

import API from "../../api";
import { capture } from "../../instrument";
import { FIELD_STATE, isRequiredFieldState } from "../../utils";

const MIN_QUERY_LENGTH = 2;
const DEBOUNCE_MS = 200;
const STALE_TIME_MS = 60_000;
const POPOVER_Z_INDEX = 60;

const formatSamplerName = (sampler) => `${sampler.firstName || ""} ${sampler.lastName || ""}`.trim();

const SamplerField = ({ code, inheritedCode = "", inheritedLabel = "", onChange, fieldState }) => {
  const [inputValue, setInputValue] = useState("");
  const [debouncedInputValue, setDebouncedInputValue] = useState("");
  const [isFocused, setIsFocused] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [userDismissed, setUserDismissed] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isKeyboardNavigating, setIsKeyboardNavigating] = useState(false);
  const [isDetailOpen, setIsDetailOpen] = useState(false);

  const inputRef = useRef(null);
  const selectedItemRef = useRef(null);

  const isInherited = code === "" && inheritedCode !== "";
  const effectiveCode = isInherited ? inheritedCode : code;

  const { data: sampler, error: samplerError } = useQuery({
    queryKey: ["clinician", { code: effectiveCode }],
    queryFn: async ({ signal }) => {
      const response = await API.get(`/clinician?code=${encodeURIComponent(effectiveCode)}`, { signal });
      if (!response.ok) throw new Error(response.message);
      return response.data[0] || null;
    },
    enabled: effectiveCode !== "",
    staleTime: STALE_TIME_MS,
  });

  useEffect(() => {
    if (samplerError) capture(samplerError);
  }, [samplerError]);

  useEffect(() => {
    if (!isEditing) return;
    inputRef.current?.focus();
    inputRef.current?.select?.();
  }, [isEditing]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedInputValue(inputValue), DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [inputValue]);

  const normalizedQuery = debouncedInputValue.trim();
  const queryEnabled = normalizedQuery.length >= MIN_QUERY_LENGTH;

  const { data, isFetching, error } = useQuery({
    queryKey: ["clinician", { q: normalizedQuery }],
    queryFn: async ({ signal }) => {
      const response = await API.get(`/clinician?q=${encodeURIComponent(normalizedQuery)}`, { signal });
      if (!response.ok) {
        toast.error(response.message || "Erreur lors de la recherche");
        return [];
      }
      return response.data.filter((clinician) => clinician.code);
    },
    enabled: queryEnabled,
    retry: false,
    staleTime: STALE_TIME_MS,
    placeholderData: keepPreviousData,
  });

  const suggestions = queryEnabled ? data || [] : [];

  useEffect(() => {
    if (!error) return;
    if (error?.name === "AbortError") return;
    capture(error);
    toast.error("Une erreur est survenue lors de la recherche du préleveur");
  }, [error]);

  useEffect(() => {
    setSelectedIndex(0);
  }, [normalizedQuery]);

  useEffect(() => {
    if (selectedItemRef.current) selectedItemRef.current.scrollIntoView({ block: "nearest" });
  }, [selectedIndex]);

  const trimmedInput = inputValue.trim();
  const hasQuery = trimmedInput.length >= MIN_QUERY_LENGTH;
  const isSearchPending = hasQuery && (trimmedInput !== normalizedQuery || isFetching);
  const showPopover = isFocused && hasQuery && !userDismissed;

  const { refs, floatingStyles, context } = useFloating({
    open: showPopover,
    onOpenChange: (next) => {
      if (next === false) setUserDismissed(true);
    },
    placement: "bottom-start",
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(0),
      flip({ padding: 8 }),
      shift({ padding: 8 }),
      size({
        padding: 8,
        apply({ rects, availableHeight, elements }) {
          Object.assign(elements.floating.style, { width: `${rects.reference.width}px`, maxHeight: `${availableHeight}px` });
        },
      }),
    ],
  });
  const { getReferenceProps, getFloatingProps } = useInteractions([useDismiss(context), useRole(context, { role: "dialog" })]);

  const detailFloating = useFloating({
    open: isDetailOpen,
    onOpenChange: setIsDetailOpen,
    placement: "bottom-end",
    whileElementsMounted: autoUpdate,
    middleware: [offset(4), flip({ padding: 8 }), shift({ padding: 8 })],
  });
  const detailInteractions = useInteractions([useClick(detailFloating.context), useDismiss(detailFloating.context), useRole(detailFloating.context)]);

  const footerVisible = hasQuery && isSearchPending === false;
  const totalItems = suggestions.length + (footerVisible ? 1 : 0);
  const footerSelected = footerVisible && selectedIndex === suggestions.length;

  const selectCode = (nextCode) => {
    onChange(nextCode);
    setInputValue("");
    setUserDismissed(false);
    setIsEditing(false);
    inputRef.current?.blur();
  };

  const startEditing = () => {
    setInputValue(code);
    setUserDismissed(false);
    setIsEditing(true);
  };

  const stopEditing = () => {
    setIsEditing(false);
    setInputValue("");
  };

  const navOpts = { enabled: showPopover && totalItems > 0, enableOnFormTags: ["INPUT"], preventDefault: true };
  useHotkeys("down", () => { setIsKeyboardNavigating(true); setSelectedIndex((i) => Math.min(totalItems - 1, i + 1)); }, navOpts, [showPopover, totalItems]);
  useHotkeys("up", () => { setIsKeyboardNavigating(true); setSelectedIndex((i) => Math.max(0, i - 1)); }, navOpts, [showPopover, totalItems]);
  useHotkeys(
    "enter",
    () => {
      if (footerSelected === true) selectCode(trimmedInput);
      else if (suggestions[selectedIndex]) selectCode(suggestions[selectedIndex].code);
    },
    navOpts,
    [showPopover, totalItems, footerSelected, suggestions, selectedIndex, trimmedInput],
  );

  const onKeyDown = (event) => {
    if (event.key === "Escape" && isEditing === true) {
      event.preventDefault();
      stopEditing();
      inputRef.current?.blur();
    }
  };

  const onPanelMouseMove = () => {
    if (isKeyboardNavigating === true) setIsKeyboardNavigating(false);
  };

  const isRequiredEmpty = isRequiredFieldState(fieldState) && effectiveCode === "";

  if (fieldState === FIELD_STATE.DISABLE) return null;

  if (effectiveCode !== "" && isEditing === false) {
    return (
      <div className="flex h-[32px] w-full items-center gap-2 rounded-md border border-transparent px-2 transition-colors hover:border-gray-200 hover:bg-gray-50">
        <span
          title={isInherited ? "Aucun préleveur saisi, ce code sera envoyé au SIL. Cliquer pour en choisir un autre" : "Modifier le code préleveur"}
          onClick={startEditing}
          className={`min-w-0 flex-1 cursor-pointer truncate text-[13px] ${isInherited ? "text-gray-400" : "text-gray-800"}`}
        >
          {sampler ? (
            <>
              {formatSamplerName(sampler)} ({effectiveCode})
              {isInherited === false && sampler.email && (
                <>
                  <span className="text-gray-300"> · </span>
                  <a href={`mailto:${sampler.email}`} onClick={(event) => event.stopPropagation()} className="text-primary-600 hover:underline">
                    {sampler.email}
                  </a>
                </>
              )}
            </>
          ) : (
            effectiveCode
          )}
        </span>

        {isInherited && <span className="chip shrink-0 border-gray-200 bg-gray-50 text-gray-500">{inheritedLabel}</span>}

        {sampler && (
          <>
            <button
              ref={detailFloating.refs.setReference}
              {...detailInteractions.getReferenceProps()}
              type="button"
              title="Détail du préleveur"
              className="shrink-0 rounded p-0.5 text-gray-400 transition-colors hover:bg-gray-200 hover:text-gray-600"
            >
              <LuInfo className="size-3.5" />
            </button>

            {isDetailOpen && (
              <FloatingPortal>
                <div
                  ref={detailFloating.refs.setFloating}
                  style={{ ...detailFloating.floatingStyles, zIndex: POPOVER_Z_INDEX }}
                  {...detailInteractions.getFloatingProps()}
                  className="w-64 rounded-lg border border-gray-200 bg-white p-3 shadow-lg"
                >
                  <p className="text-sm font-medium text-gray-900">{formatSamplerName(sampler)}</p>
                  <p className="mt-0.5 text-xs text-gray-500">Code préleveur {effectiveCode}</p>
                  <div className="mt-3 flex flex-col gap-1.5 text-xs">
                    <div className="flex gap-2">
                      <span className="w-16 shrink-0 text-gray-500">Email</span>
                      {sampler.email ? (
                        <a href={`mailto:${sampler.email}`} className="min-w-0 truncate text-primary-600 hover:underline">
                          {sampler.email}
                        </a>
                      ) : (
                        <span className="text-gray-400">Non renseigné</span>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <span className="w-16 shrink-0 text-gray-500">Téléphone</span>
                      {sampler.phone ? (
                        <a href={`tel:${sampler.phone}`} className="min-w-0 truncate text-primary-600 hover:underline">
                          {sampler.phone}
                        </a>
                      ) : (
                        <span className="text-gray-400">Non renseigné</span>
                      )}
                    </div>
                  </div>
                </div>
              </FloatingPortal>
            )}
          </>
        )}

        {isInherited === false && (
          <button
            type="button"
            title="Supprimer le code préleveur"
            onClick={() => onChange("")}
            className="shrink-0 rounded p-0.5 text-gray-400 transition-colors hover:bg-gray-200 hover:text-gray-600"
          >
            <LuX className="size-3.5" />
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="flex min-w-0 flex-1 flex-col gap-1">
      <div
        ref={refs.setReference}
        {...getReferenceProps()}
        className={`field-shell${showPopover ? " rounded-b-none" : ""}`}
        data-state={isRequiredEmpty ? "required" : undefined}
      >
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={(event) => {
            setInputValue(event.target.value);
            setUserDismissed(false);
          }}
          onKeyDown={onKeyDown}
          onFocus={() => {
            setIsFocused(true);
            setUserDismissed(false);
            setIsEditing(true);
          }}
          onBlur={() =>
            setTimeout(() => {
              setIsFocused(false);
              stopEditing();
            }, 150)
          }
          placeholder="Nom ou code du préleveur"
          aria-label="Rechercher un préleveur"
          className={`min-w-0 flex-1 bg-transparent text-sm text-gray-800 outline-none ${isRequiredEmpty ? "placeholder:text-orange-400" : "placeholder:text-gray-400"}`}
          autoComplete="off"
        />
        {isSearchPending ? <div className="field-spinner" /> : <LuSearch className="size-4 shrink-0 text-gray-400" />}
      </div>

      {showPopover && (
        <FloatingPortal>
          <div ref={refs.setFloating} style={{ ...floatingStyles, zIndex: POPOVER_Z_INDEX }} className="flex flex-col" {...getFloatingProps()}>
            <div className="suggestion-panel" data-modal="true" onMouseMove={onPanelMouseMove}>
              {suggestions.length > 0 && (
                <ul role="list" className="min-h-0 flex-1 overflow-auto py-1">
                  {suggestions.map((clinician, index) => {
                    const isSelected = index === selectedIndex;
                    return (
                      <li
                        key={clinician._id}
                        ref={isSelected ? selectedItemRef : null}
                        onMouseDown={(event) => {
                          event.preventDefault();
                          selectCode(clinician.code);
                        }}
                        onMouseEnter={() => {
                          if (isKeyboardNavigating === false) setSelectedIndex(index);
                        }}
                        className="suggestion-item"
                        data-selected={isSelected}
                      >
                        <div className="flex min-w-0 flex-col px-3 py-1.5">
                          <div className="flex items-baseline gap-2 truncate">
                            <span className={`text-sm font-semibold ${isSelected ? "text-primary-700" : "text-gray-900"}`}>{formatSamplerName(clinician)}</span>
                            <span className="truncate text-xs text-gray-600">{clinician.code}</span>
                          </div>
                          {clinician.email && <span className="mt-0.5 truncate text-[11px] text-gray-400">{clinician.email}</span>}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}

              {isSearchPending && suggestions.length === 0 && <div className="suggestion-empty">Recherche…</div>}

              {footerVisible === true && (
                <button
                  type="button"
                  onMouseDown={(event) => {
                    event.preventDefault();
                    selectCode(trimmedInput);
                  }}
                  onMouseEnter={() => {
                    if (isKeyboardNavigating === false) setSelectedIndex(suggestions.length);
                  }}
                  className="suggestion-footer"
                  data-selected={footerSelected}
                >
                  <span className="suggestion-footer-badge">+</span>
                  <span className="truncate">Utiliser « {trimmedInput} » comme code</span>
                </button>
              )}
            </div>
          </div>
        </FloatingPortal>
      )}
    </div>
  );
};

export default SamplerField;
